import React, { useEffect, useMemo, useState } from 'react';
import { MapPin, CloudSun, Loader, AlertCircle, Wind, Gauge, Eye, Droplet, Thermometer, Compass, Search } from 'lucide-react';

const Weather = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [data, setData] = useState(null);
  const [extra, setExtra] = useState(null);
  const [air, setAir] = useState(null);
  const [airSeries, setAirSeries] = useState(null);
  const [tz, setTz] = useState(null);
  const [now, setNow] = useState(Date.now());

  // ... (keeping fetch functions identical as they work well, just changing UI structure) ...
  const fetchWeather = async (lat, lon) => {
    try {
      setLoading(true);
      setError(null);
      const wres = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true&timezone=auto`);
      const wjson = await wres.json();
      setData(wjson.current_weather);
      setTz(wjson.timezone || null);

      const eres = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=apparent_temperature,relative_humidity_2m,surface_pressure,wind_direction_10m&hourly=visibility&timezone=auto`);
      const ejson = await eres.json();
      const vis = ejson.hourly && ejson.hourly.visibility && ejson.hourly.visibility[0];
      setExtra({
        apparent_temperature: ejson.current?.apparent_temperature,
        relative_humidity_2m: ejson.current?.relative_humidity_2m,
        surface_pressure: ejson.current?.surface_pressure,
        wind_direction_10m: ejson.current?.wind_direction_10m,
        visibility: vis,
      });

      const aq = await fetch(`https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${lat}&longitude=${lon}&hourly=pm10,pm2_5,carbon_monoxide,sulphur_dioxide&timezone=auto`);
      const aqjson = await aq.json();
      const pick = (arr) => (arr && arr.length ? arr[0] : null);
      setAir({
        pm25: pick(aqjson.hourly?.pm2_5),
        pm10: pick(aqjson.hourly?.pm10),
        co: pick(aqjson.hourly?.carbon_monoxide),
        so2: pick(aqjson.hourly?.sulphur_dioxide),
      });
      const take = (arr, n) => (Array.isArray(arr) ? arr.slice(0, n) : []);
      const n = 24;
      setAirSeries({
        time: take(aqjson.hourly?.time, n),
        pm25: take(aqjson.hourly?.pm2_5, n),
        pm10: take(aqjson.hourly?.pm10, n),
        co: take(aqjson.hourly?.carbon_monoxide, n),
        so2: take(aqjson.hourly?.sulphur_dioxide, n),
      });
    } catch (e) {
      setError('Weather fetch failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => fetchWeather(pos.coords.latitude, pos.coords.longitude),
        () => { }
      );
    }
  }, []);

  const searchCity = async () => {
    if (!query.trim()) return;
    try {
      setLoading(true);
      setError(null);
      const geo = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query)}&count=1&language=en&format=json`);
      const gjson = await geo.json();
      if (gjson.results && gjson.results[0]) {
        const { latitude, longitude } = gjson.results[0];
        await fetchWeather(latitude, longitude);
      } else {
        setError('City not found');
      }
    } catch (e) {
      setError('Search failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Formatters
  const timeString = useMemo(() => {
    try {
      return new Intl.DateTimeFormat('en-GB', { timeZone: tz || undefined, hour: '2-digit', minute: '2-digit' }).format(new Date(now));
    } catch {
      return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' }).format(new Date(now));
    }
  }, [tz, now]);

  const dateString = useMemo(() => {
    try {
      return new Intl.DateTimeFormat('en-GB', { timeZone: tz || undefined, fileName: 'long', day: 'numeric', month: 'long', weekday: 'long' }).format(new Date(now));
    } catch {
      return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', weekday: 'long' }).format(new Date(now));
    }
  }, [tz, now]);

  const degToCompass = (deg) => {
    if (deg == null) return '-';
    const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const ix = Math.floor(((deg % 360) / 22.5) + 0.5);
    return dirs[ix % 16];
  };

  const aqStatus = (v) => {
    if (v == null) return { label: 'Unknown', color: 'bg-slate-100 text-slate-600' };
    if (v <= 12) return { label: 'Good', color: 'bg-emerald-100 text-emerald-700' };
    if (v <= 35.4) return { label: 'Moderate', color: 'bg-yellow-100 text-yellow-700' };
    if (v <= 55.4) return { label: 'Sensitive', color: 'bg-orange-100 text-orange-700' };
    if (v <= 150.4) return { label: 'Unhealthy', color: 'bg-red-100 text-red-700' };
    return { label: 'Hazardous', color: 'bg-purple-100 text-purple-700' };
  };

  const barWidth = (val, max) => {
    if (val == null || max <= 0) return '0%';
    const pct = Math.min(100, Math.round((val / max) * 100));
    return pct + '%';
  };

  return (
    <div className="min-h-screen bg-transparent py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">

        {/* Header Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-black text-slate-800 tracking-tight">Weather Station</h1>
            <p className="text-slate-500">Real-time hyper-local agricultural forecasts</p>
          </div>

          <div className="relative w-full md:w-96 group">
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search city..."
              className="w-full bg-white border border-slate-200 rounded-2xl py-3 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition shadow-sm group-hover:shadow-md"
              onKeyDown={(e) => e.key === 'Enter' && searchCity()}
            />
            <Search className="w-5 h-5 text-slate-400 absolute left-4 top-3.5" />
            <button
              onClick={searchCity}
              className="absolute right-2 top-2 bg-emerald-600 text-white p-1.5 rounded-xl hover:bg-emerald-700 transition"
            >
              <div className="w-5 h-5 flex items-center justify-center font-bold">→</div>
            </button>
          </div>
        </div>

        {loading && (
          <div className="text-center py-20">
            <Loader className="w-10 h-10 text-emerald-600 animate-spin mx-auto mb-4" />
            <p className="text-slate-500 font-medium">Fetching satellite data...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex items-center text-red-700 mb-8 max-w-lg mx-auto">
            <AlertCircle className="w-5 h-5 mr-3" />
            {error}
          </div>
        )}

        {data && !loading && (
          <div className="space-y-6 animate-fade-in-up">

            {/* Main Weather Card */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

              {/* Current Status */}
              <div className="lg:col-span-2 bg-gradient-to-br from-blue-500 to-blue-700 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>

                <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center">
                  <div>
                    <div className="flex items-center space-x-2 text-blue-100 mb-1">
                      <MapPin className="w-4 h-4" />
                      <span className="font-medium tracking-wide">{tz || 'Local Location'}</span>
                    </div>
                    <div className="text-blue-200 text-sm font-medium mb-6 uppercase tracking-wider">{dateString}</div>

                    <div className="flex items-center">
                      <span className="text-8xl font-black tracking-tighter">{Math.round(data.temperature)}°</span>
                      <div className="ml-6">
                        <CloudSun className="w-12 h-12 text-yellow-300 mb-2" />
                        <div className="text-xl font-medium">Clear Sky</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 md:mt-0 text-right">
                    <div className="text-5xl font-mono font-bold opacity-90">{timeString}</div>
                    <div className="mt-4 flex flex-col items-end space-y-2">
                      <div className="flex items-center space-x-2 bg-white/20 px-3 py-1.5 rounded-lg backdrop-blur-sm">
                        <Wind className="w-4 h-4" />
                        <span className="font-bold">{Math.round(data.windspeed)} km/h</span>
                      </div>
                      <div className="flex items-center space-x-2 bg-white/20 px-3 py-1.5 rounded-lg backdrop-blur-sm">
                        <Compass className="w-4 h-4" />
                        <span className="font-bold">{data.winddirection}° {degToCompass(data.winddirection)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Detail Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100 flex flex-col justify-center">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 mb-3"><Thermometer className="w-6 h-6" /></div>
                  <div className="text-slate-500 text-xs font-bold uppercase tracking-wider">Feels Like</div>
                  <div className="text-2xl font-black text-slate-800">{extra?.apparent_temperature ? Math.round(extra.apparent_temperature) + '°' : '-'}</div>
                </div>
                <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100 flex flex-col justify-center">
                  <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center text-sky-600 mb-3"><Droplet className="w-6 h-6" /></div>
                  <div className="text-slate-500 text-xs font-bold uppercase tracking-wider">Humidity</div>
                  <div className="text-2xl font-black text-slate-800">{extra?.relative_humidity_2m ? Math.round(extra.relative_humidity_2m) + '%' : '-'}</div>
                </div>
                <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100 flex flex-col justify-center">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600 mb-3"><Gauge className="w-6 h-6" /></div>
                  <div className="text-slate-500 text-xs font-bold uppercase tracking-wider">Pressure</div>
                  <div className="text-xl font-black text-slate-800">{extra?.surface_pressure ? Math.round(extra.surface_pressure) : '-'} <span className="text-xs text-slate-400 font-normal">hPa</span></div>
                </div>
                <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100 flex flex-col justify-center">
                  <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600 mb-3"><Eye className="w-6 h-6" /></div>
                  <div className="text-slate-500 text-xs font-bold uppercase tracking-wider">Visibility</div>
                  <div className="text-xl font-black text-slate-800">{extra?.visibility ? (extra.visibility / 1000).toFixed(1) : '-'} <span className="text-xs text-slate-400 font-normal">km</span></div>
                </div>
              </div>

            </div>

            {/* Air Quality Section */}
            {air && (
              <div className="bg-white rounded-3xl shadow-xl p-8 border border-slate-100">
                <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center">
                  Air Quality Index
                  {air.pm25 && (
                    <span className={`ml-4 px-3 py-1 rounded-full text-xs font-bold uppercase ${aqStatus(air.pm25).color}`}>
                      {aqStatus(air.pm25).label}
                    </span>
                  )}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <AQIPill label="PM 2.5" value={air.pm25} max={100} unit="µg/m³" color="bg-emerald-500" />
                  <AQIPill label="PM 10" value={air.pm10} max={200} unit="µg/m³" color="bg-blue-500" />
                  <AQIPill label="CO" value={air.co} max={1000} unit="µg/m³" color="bg-orange-500" />
                  <AQIPill label="SO2" value={air.so2} max={200} unit="µg/m³" color="bg-purple-500" />
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

const AQIPill = ({ label, value, max, unit, color }) => (
  <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
    <div className="flex justify-between items-end mb-2">
      <span className="text-slate-500 text-sm font-semibold">{label}</span>
      <span className="text-slate-900 font-bold">{value?.toFixed(1) || '-'} <span className="text-xs font-normal text-slate-400">{unit}</span></span>
    </div>
    <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(100, (value / max) * 100)}%` }}></div>
    </div>
  </div>
);

export default Weather;
